import { useState, useEffect, useRef } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Camera, X, Check, Loader2, User, Hand, Sparkles } from "lucide-react";
import * as LucideIcons from "lucide-react";

const MAX_IMAGE_SIZE = 256; // Target max dimension for compressed image
const MAX_FILE_SIZE = 500 * 1024; // 500KB limit for upload

/**
 * Compresses and resizes an image file to a data URI
 */
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Scale down to max dimension while preserving aspect ratio
        if (width > height) {
          if (width > MAX_IMAGE_SIZE) {
            height = Math.round((height * MAX_IMAGE_SIZE) / width);
            width = MAX_IMAGE_SIZE;
          }
        } else {
          if (height > MAX_IMAGE_SIZE) {
            width = Math.round((width * MAX_IMAGE_SIZE) / height);
            height = MAX_IMAGE_SIZE;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Start with high quality and reduce if needed
        let quality = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        
        // Reduce quality until under size limit
        while (dataUrl.length > MAX_FILE_SIZE && quality > 0.3) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }

        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const ProfileEditModal = ({ open, onOpenChange, user, onProfileUpdated, isForcePrompt = false }) => {
  const [photoUrl, setPhotoUrl] = useState(user?.photo_url || "");
  const [playingHand, setPlayingHand] = useState(user?.playing_hand || "");
  const [selectedSpecialities, setSelectedSpecialities] = useState(user?.specialities || []);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchSpecialities();
      // Reset form to user's current values when opening
      setPhotoUrl(user?.photo_url || "");
      setPlayingHand(user?.playing_hand || "");
      setSelectedSpecialities(user?.specialities || []);
    }
  }, [open, user]);

  const fetchSpecialities = async () => {
    try {
      const response = await apiClient.get("/specialities");
      setSpecialitiesList(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error("Failed to fetch specialities:", error);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setUploading(true);
    try {
      const compressedDataUrl = await compressImage(file);
      setPhotoUrl(compressedDataUrl);
      toast.success("Photo uploaded and compressed");
    } catch (error) {
      toast.error("Failed to process image");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoUrl("");
  };

  const toggleSpeciality = (specId) => {
    setSelectedSpecialities(prev => {
      if (prev.includes(specId)) {
        return prev.filter(id => id !== specId);
      } else if (prev.length < 5) {
        return [...prev, specId];
      } else {
        toast.error("You can select up to 5 specialities");
        return prev;
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        photo_url: photoUrl || null,
        playing_hand: playingHand || null,
        specialities: selectedSpecialities,
      };

      const response = await apiClient.put("/me/profile", payload);
      toast.success("Profile updated successfully!");
      onProfileUpdated?.(response.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const isComplete = photoUrl && playingHand && selectedSpecialities.length > 0;

  // Get dynamic icon component
  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={isForcePrompt ? () => {} : onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <User className="w-5 h-5 text-[#064E3B]" />
            {isForcePrompt ? "Complete Your Profile" : "Edit Profile"}
          </DialogTitle>
          <DialogDescription>
            {isForcePrompt 
              ? "Please complete your player profile to get started with Turf Tribe."
              : "Update your player profile information."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Photo Upload Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Camera className="w-4 h-4" />
              Profile Photo
            </Label>
            <div className="flex items-center gap-4">
              <div className="relative">
                {photoUrl ? (
                  <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-[#064E3B]/20">
                    <img 
                      src={photoUrl} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={handleRemovePhoto}
                      className="absolute top-0 right-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      data-testid="remove-photo-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-dashed border-slate-300 flex items-center justify-center">
                    <User className="w-10 h-10 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-xl"
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      {photoUrl ? "Change Photo" : "Upload Photo"}
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  JPG, PNG up to 5MB. Will be resized to 256x256px.
                </p>
              </div>
            </div>
          </div>

          {/* Playing Hand Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Hand className="w-4 h-4" />
              Playing Hand
            </Label>
            <Select value={playingHand} onValueChange={setPlayingHand}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="playing-hand-select">
                <SelectValue placeholder="Select your dominant hand" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="right">Right Hand</SelectItem>
                <SelectItem value="left">Left Hand</SelectItem>
                <SelectItem value="both">Ambidextrous (Both)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Specialities Section */}
          <div className="space-y-3">
            <Label className="flex items-center justify-between text-sm font-semibold">
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Your Specialities
              </span>
              <span className="text-xs font-normal text-slate-500">
                {selectedSpecialities.length}/5 selected
              </span>
            </Label>
            <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto p-1">
              {specialitiesList.map((spec) => {
                const isSelected = selectedSpecialities.includes(spec.id);
                return (
                  <button
                    key={spec.id}
                    type="button"
                    onClick={() => toggleSpeciality(spec.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl text-left text-sm transition-all border-2 ${
                      isSelected
                        ? "bg-[#064E3B] text-white border-[#064E3B]"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:border-[#064E3B]/50"
                    }`}
                    data-testid={`speciality-${spec.id}`}
                  >
                    <span className={isSelected ? "text-[#BEF264]" : "text-[#064E3B]"}>
                      {getIcon(spec.icon)}
                    </span>
                    <span className="truncate flex-1">{spec.name}</span>
                    {isSelected && <Check className="w-4 h-4 text-[#BEF264]" />}
                  </button>
                );
              })}
            </div>
            {specialitiesList.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">
                No specialities available yet.
              </p>
            )}
          </div>

          {/* Completion Status */}
          {isForcePrompt && (
            <div className={`p-3 rounded-xl border ${
              isComplete ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"
            }`}>
              <p className={`text-sm font-medium ${isComplete ? "text-green-700" : "text-amber-700"}`}>
                {isComplete 
                  ? "Your profile is complete! Click Save to continue."
                  : "Please fill in all fields to complete your profile."
                }
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!isForcePrompt && (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl"
              data-testid="profile-cancel-btn"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={loading || (isForcePrompt && !isComplete)}
            className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
            data-testid="profile-save-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileEditModal;
